import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";

const ImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: auto;
  border-radius: ${(props) => props.borderRadius || "8px"};
`;

const Image = styled.img`
  width: 100%;
  height: auto;
  transition: all 0.4s ease;
  opacity: ${(props) => (props.loaded ? 1 : 0)};
  transform: ${(props) => (props.loaded ? "scale(1)" : "scale(1.05)")};
  object-fit: cover;
  border-radius: inherit;
`;

const Placeholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: ${(props) => props.height || "200px"};
  background: linear-gradient(
    110deg,
    ${(props) => props.theme.carouselColor} 8%,
    ${(props) => `rgba(${props.theme.textRgba}, 0.08)`} 18%,
    ${(props) => props.theme.carouselColor} 33%
  );
  background-size: 200% 100%;
  animation: ${(props) =>
    props.loaded ? "none" : "shimmer 1.8s infinite ease-in-out"};
  display: ${(props) => (props.loaded ? "none" : "flex")};
  align-items: center;
  justify-content: center;
  color: ${(props) => `rgba(${props.theme.textRgba}, 0.6)`};
  font-size: ${(props) => props.theme.fontsm};
  font-weight: 500;
  border-radius: inherit;
  backdrop-filter: blur(1px);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      45deg,
      transparent 30%,
      rgba(255, 255, 255, 0.1) 50%,
      transparent 70%
    );
    transform: translateX(-100%);
    animation: ${(props) => (props.loaded ? "none" : "shine 2s infinite")};
  }

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  @keyframes shine {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${(props) => `rgba(${props.theme.textRgba}, 0.2)`};
  border-top: 2px solid ${(props) => props.theme.text};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 8px;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0;
  animation: fadeIn 0.5s ease forwards 0.5s;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ErrorPlaceholder = styled.div`
  width: 100%;
  height: ${(props) => props.height || "200px"};
  background: linear-gradient(
    135deg,
    ${(props) => props.theme.carouselColor} 0%,
    ${(props) => `rgba(${props.theme.textRgba}, 0.05)`} 100%
  );
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${(props) => `rgba(${props.theme.textRgba}, 0.6)`};
  font-size: ${(props) => props.theme.fontsm};
  border: 1px dashed ${(props) => `rgba(${props.theme.textRgba}, 0.2)`};
  border-radius: inherit;
  position: relative;
  overflow: hidden;

  &::before {
    content: "⚠️";
    font-size: 24px;
    margin-bottom: 8px;
    opacity: 0.7;
  }

  &::after {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: repeating-linear-gradient(
      45deg,
      transparent,
      transparent 10px,
      ${(props) => `rgba(${props.theme.textRgba}, 0.02)`} 10px,
      ${(props) => `rgba(${props.theme.textRgba}, 0.02)`} 20px
    );
    animation: drift 20s linear infinite;
  }

  @keyframes drift {
    0% {
      transform: translate(-50%, -50%) rotate(0deg);
    }
    100% {
      transform: translate(-50%, -50%) rotate(360deg);
    }
  }
`;

const OptimizedImage = ({
  src,
  webpSrc,
  alt = "",
  width,
  height,
  loading = "lazy",
  placeholder = "Loading...",
  errorText = "Failed to load image",
  className,
  borderRadius,
  ...props
}) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [inView, setInView] = useState(false);
  const imgRef = useRef();

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (loading !== "lazy") {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "50px",
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [loading]);

  const handleLoad = () => {
    setLoaded(true);
    setError(false);
  };

  const handleError = () => {
    setError(true);
    setLoaded(true);
  };

  if (error) {
    return (
      <ErrorPlaceholder
        height={height}
        className={className}
        borderRadius={borderRadius}
      >
        {errorText}
      </ErrorPlaceholder>
    );
  }

  return (
    <ImageContainer
      ref={imgRef}
      className={className}
      borderRadius={borderRadius}
      {...props}
    >
      {inView ? (
        <>
          <picture>
            {webpSrc && <source srcSet={webpSrc} type="image/webp" />}
            <Image
              src={src}
              alt={alt}
              width={width}
              height={height}
              loaded={loaded}
              onLoad={handleLoad}
              onError={handleError}
            />
          </picture>
          <Placeholder loaded={loaded} height={height}>
            <LoadingText>
              <LoadingSpinner />
              {placeholder}
            </LoadingText>
          </Placeholder>
        </>
      ) : (
        <Placeholder loaded={false} height={height}>
          <LoadingText>
            <LoadingSpinner />
            {placeholder}
          </LoadingText>
        </Placeholder>
      )}
    </ImageContainer>
  );
};

export default OptimizedImage;
